/**
 * PPR-3 B · 上架中心「手机站」→ 线路展示清单每行「编辑展示」抽屉。
 * 原产品详情「手机站展示」卡整张挪到这里（用户 10-09 定）：**字段、校验、接口全部沿用 SITE-E1**——
 * 对客标题 / 一句卖点 / 含接送机 / 小团 + 人数上限 / 季节说明，共 6 个字段，`PATCH /api/v1/products/{id}` 只提交这 6 个，绝不整体 PUT；文字清空 = null。
 * 只给手机站用，不影响同行站和平台。
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getProduct, updateProduct } from "@/services/products";
import { Drawer } from "@/pages/sitePayments/ui";

interface Draft { public_title: string; public_tagline: string; is_transfer: boolean; is_small_group: boolean; small_group_max: string; seasonal_notes: string }

export function MobileDisplayDrawer({ productId, code, onClose, onSaved }: { productId: string; code: string; onClose: () => void; onSaved: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["products", "detail", productId], queryFn: () => getProduct(productId) });
  const [d, setD] = useState<Draft | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const p = q.data;
    if (p && !d) setD({
      public_title: p.public_title ?? "", public_tagline: p.public_tagline ?? "", is_transfer: !!p.is_transfer, is_small_group: !!p.is_small_group,
      small_group_max: p.small_group_max != null ? String(p.small_group_max) : "", seasonal_notes: p.seasonal_notes ?? "",
    });
  }, [q.data, d]);
  const save = useMutation({
    mutationFn: () => {
      const x = d!;
      const max = x.small_group_max.trim() === "" ? null : Number(x.small_group_max);
      return updateProduct(productId, {
        public_title: x.public_title.trim() || null, public_tagline: x.public_tagline.trim() || null,
        is_transfer: x.is_transfer, is_small_group: x.is_small_group,
        small_group_max: x.is_small_group && max != null && Number.isInteger(max) && max >= 1 && max <= 99 ? max : null,
        seasonal_notes: x.seasonal_notes.trim() || null,
      });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["products", "detail", productId] }); onSaved(); onClose(); },
    onError: (e) => setErr(e instanceof Error ? e.message : "保存失败，请重试"),
  });
  const p = q.data;
  const set = (patch: Partial<Draft>) => d && setD({ ...d, ...patch });
  const inp = "rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
  return (
    <Drawer title={`编辑手机站展示 · ${code}`} onClose={onClose}>
      <p className="text-[12px] text-slate-500">只给手机站用，不影响同行站和平台（字段和原产品详情「手机站展示」卡一模一样，只是换了位置）</p>
      {q.isError && <button type="button" onClick={() => q.refetch()} className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">加载失败，点击重试</button>}
      {!d && !q.isError && <p className="text-sm text-slate-400">加载中…</p>}
      {d && p && (
        <div className="flex flex-col gap-4" data-testid="mobile-display-form">
          <label className="flex flex-col gap-1"><span className="text-xs font-medium text-slate-600">对客标题（不填 = 用产品中文名）</span>
            <input maxLength={120} aria-label="对客标题" className={inp} value={d.public_title} onChange={(e) => set({ public_title: e.target.value })} placeholder={p.name_cn} /></label>
          <label className="flex flex-col gap-1"><span className="text-xs font-medium text-slate-600">一句卖点（选填）</span>
            <input maxLength={200} aria-label="一句卖点" className={inp} value={d.public_tagline} onChange={(e) => set({ public_tagline: e.target.value })} placeholder="如：3 天深度游黄石 · 住园内木屋 · 中文导游" /></label>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-slate-600">分类</span>
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
              <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-[12.5px] text-slate-600">{(p.total_days ?? 0) <= 1 ? "一日游" : "多日游"}（按天数自动）</span>
              <label className="inline-flex cursor-pointer items-center gap-1.5"><input type="checkbox" checked={d.is_transfer} onChange={(e) => set({ is_transfer: e.target.checked })} />接送机</label>
            </div>
            <span className="text-[11px] text-slate-400">多日游 / 一日游按天数自动分，只有「接送机」要手选</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-slate-600">小团</span>
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-700">
              <label className="inline-flex cursor-pointer items-center gap-1.5"><input type="checkbox" checked={d.is_small_group} onChange={(e) => set({ is_small_group: e.target.checked })} />是小团</label>
              <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">人数上限
                <input type="number" min={1} max={99} disabled={!d.is_small_group} aria-label="小团人数上限" value={d.small_group_max} onChange={(e) => set({ small_group_max: e.target.value })}
                  className="w-20 rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-300" /></label>
            </div>
            <span className="text-[11px] text-slate-400">勾了才出现在首页「小团」分类，卡片上显示「N 人小团」</span>
          </div>
          <label className="flex flex-col gap-1"><span className="text-xs font-medium text-slate-600">季节说明（选填 · 显示在详情页「出行须知」里）</span>
            <textarea rows={3} aria-label="季节说明" className={inp} value={d.seasonal_notes} onChange={(e) => set({ seasonal_notes: e.target.value })}
              placeholder="如：黄石东门约 5/2 开放，之前大峡谷、海登山谷等景点去不了。" /></label>
          {err && <p className="text-xs text-rose-600">{err}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-slate-300 bg-white px-3.5 py-1.5 text-[13px]">取消</button>
            <button type="button" data-testid="mobile-display-save" disabled={save.isPending} onClick={() => save.mutate()} className="rounded-md bg-blue-600 px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-50">{save.isPending ? "保存中…" : "保存"}</button>
          </div>
          <p className="text-[11.5px] text-slate-400">PATCH /api/v1/products/{"{id}"} 只提交这 6 个字段 · 保存后左边预览自动刷新</p>
        </div>
      )}
    </Drawer>
  );
}
